/**
 * Military bases static data source.
 * Loads 220+ global military installation locations from built-in config.
 * No API proxy needed — all data is embedded in the application.
 * Public domain OSINT data. Updated once per day (static, no upstream).
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, EntityReference } from '../base-source';
import { MILITARY_BASES, type MilitaryBase } from '@/config/military-bases';

const SOURCE_ID = 'military-bases';

function baseTypeToLabel(type: MilitaryBase['type']): string {
  switch (type) {
    case 'army': return 'Army';
    case 'navy': return 'Navy';
    case 'airforce': return 'Air Force';
    case 'joint': return 'Joint';
    case 'marine': return 'Marine Corps';
    case 'missile': return 'Missile';
    case 'intelligence': return 'Intelligence';
    default: return 'Military';
  }
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const militaryBasesSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Military Bases',
    description: 'Static military base locations from built-in config (220+ bases)',
    icon: 'shield',
    category: 'government',
    updateInterval: 86_400_000, // 24 hours — static data rarely changes
    requiresApiKey: false,
    dataLicense: 'Public Domain (OSINT)',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Military bases source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      const events: NormalizedEvent[] = [];

      for (const base of MILITARY_BASES) {
        const entity: EntityReference = {
          entityId: base.id,
          name: base.name,
          type: 'facility',
          confidence: 1.0,
        };

        events.push({
          id: base.id,
          source: SOURCE_ID,
          timestamp: new Date().toISOString(),
          type: 'military',
          title: `${base.name} — ${baseTypeToLabel(base.type)}`,
          description: base.description,
          severity: 'info',
          location: {
            lat: base.lat,
            lng: base.lng,
            country: base.country,
            countryCode: base.countryCode,
            name: base.name,
          },
          entities: [entity],
          tags: ['military-base', base.type, base.operator, base.countryCode],
          raw: {
            operator: base.operator,
            type: base.type,
            country: base.country,
            countryCode: base.countryCode,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
